import { ArrowLeftIcon, EnvelopeIcon, PhoneIcon, HomeIcon, MapPinIcon, CurrencyDollarIcon, CalculatorIcon, BuildingOfficeIcon, DocumentTextIcon, PlusIcon, CheckCircleIcon, CalendarIcon, LightBulbIcon, ExclamationTriangleIcon, InformationCircleIcon, CheckBadgeIcon, BookOpenIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { VisitorMetadata } from '@/components/leads/VisitorMetadata';
import { PropertyImageUpload } from '@/components/PropertyImageUpload';
import { MapyCzPanoramaInteractive } from '@/components/MapyCzPanoramaInteractive';
import { DocumentViewerModal } from '@/components/documents/DocumentViewerModal';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Card, Badge, Spinner, Button, Avatar, Input } from '@realvisor/ui';
import { useLead, useUpdateLead, useActivities, useCreateActivity, useLeadStageHistory, useDocuments, ACTIVITY_TYPE_LABELS, type ActivityType, ValuoSourcePayload } from '@/hooks';
import { useSip } from '@/contexts/SipContext';
import { usePipeline } from '@/hooks/usePipelines';
import { analyzeProperty, getSeverityBgClass, getSeverityTextClass, getSeverityIconClass, type PropertyInsight, type InsightSeverity } from '@/utils/propertyAnalysis';

function formatValue(value: number | null | undefined): string {
  if (!value) return '-';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M Kč`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K Kč`;
  return `${value.toLocaleString()} Kč`;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('cs-CZ').format(price) + ' Kč';
}

function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Invalid date format:', dateString, error);
    return '-';
  }
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('cs-CZ').format(value);
}

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  flat: 'Byt',
  house: 'Dům',
  land: 'Pozemek',
};

const RATING_LABELS: Record<string, string> = {
  bad: 'Špatný',
  nothing_much: 'Nic moc',
  good: 'Dobrý',
  very_good: 'Velmi dobrý',
  new: 'Novostavba',
  excellent: 'Excelentní',
};

const OWNERSHIP_LABELS: Record<string, string> = {
  private: 'Osobní',
  cooperative: 'Družstevní',
  council: 'Obecní',
  other: 'Jiné',
};

const HOUSE_TYPE_LABELS: Record<string, string> = {
  family_house: 'Rodinný dům',
  cottage: 'Chalupa',
  villa: 'Vila',
  hut: 'Chata',
  other: 'Jiné',
};

const LAND_TYPE_LABELS: Record<string, string> = {
  building: 'Stavební',
  field: 'Pole',
  garden: 'Zahrada',
  meadow: 'Louka',
  forest: 'Les',
  other: 'Jiné',
};

const CONSTRUCTION_LABELS: Record<string, string> = {
  brick: 'Cihla',
  panel: 'Panel',
  wood: 'Dřevo',
  stone: 'Kámen',
  montage: 'Montovaný',
  mixed: 'Smíšený',
  low_energy: 'Nízkoenergetický',
  other: 'Jiné',
};

const LEAD_TYPE_LABELS: Record<string, string> = {
  sale: 'Prodej',
  purchase: 'Nákup',
  rent: 'Pronájem',
  hypo: 'Hypotéka',
  invest: 'Investice',
  pm: 'Správa nemovitostí',
  other: 'Jiné',
};

const LEAD_STATUS_LABELS: Record<string, string> = {
  open: 'Otevřený',
  won: 'Vyhráno',
  lost: 'Prohráno',
  archived: 'Archivováno',
  spam: 'Spam',
};

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('cs-CZ', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch (error) {
    console.error('Invalid date format:', dateString, error);
    return '-';
  }
}

const LEAD_STATUSES = [
  { value: 'open', label: 'Otevřený', color: '#3b82f6' },
  { value: 'won', label: 'Vyhráno', color: '#10b981' },
  { value: 'lost', label: 'Prohráno', color: '#ef4444' },
  { value: 'archived', label: 'Archivováno', color: '#6b7280' },
  { value: 'spam', label: 'Spam', color: '#f59e0b' },
];

const ACTIVITY_TYPES: { value: ActivityType; label: string }[] = [
  { value: 'call', label: 'Hovor' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Schůzka' },
  { value: 'note', label: 'Poznámka' },
  { value: 'task', label: 'Úkol' },
  { value: 'owner_verification', label: 'Ověření vlastníka' },
];

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: lead, isLoading, error } = useLead(id || '');
  const { data: activities, isLoading: activitiesLoading } = useActivities({ leadId: id });
  const { data: stageHistory } = useLeadStageHistory(id || '');
  const { data: pipeline } = usePipeline(lead?.pipelineId || '');
  const updateLead = useUpdateLead();
  const createActivity = useCreateActivity();
  const sip = useSip();

  // Načíst relevantní návody (obecné + pro projekt leadu)
  const { data: documents } = useDocuments({ 
    projectId: lead?.projectId || undefined
  });

  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activityType, setActivityType] = useState<ActivityType>('note');
  const [activitySubject, setActivitySubject] = useState('');
  const [activityBody, setActivityBody] = useState('');
  const [propertyImages, setPropertyImages] = useState<any[]>([]);
  const [isOwner, setIsOwner] = useState<boolean | null>(null);
  const [knData, setKnData] = useState('');
  const [viewingDocument, setViewingDocument] = useState<any>(null);

  const handleStageChange = async (newStageId: string) => {
    if (!id || newStageId === lead?.currentStageId) return;
    try {
      await updateLead.mutateAsync({ id, data: { stageId: newStageId } });
      toast.success('Fáze aktualizována');
    } catch (err) {
      toast.error('Nepodařilo se změnit fázi');
    }
  };

  const handleCreateActivity = async () => {
    if (!id || !activitySubject.trim()) return;
    
    // Pro owner_verification vyžadovat výběr
    if (activityType === 'owner_verification' && isOwner === null) {
      toast.error('Vyberte, zda je kontakt vlastníkem');
      return;
    }

    try {
      let finalBody = activityBody || '';
      
      // Přidat data pro owner verification do body
      if (activityType === 'owner_verification') {
        const ownerStatus = isOwner ? 'Je vlastník' : 'Není vlastník';
        finalBody = `${ownerStatus}\n\n${activityBody || ''}`;
        
        if (knData.trim()) {
          finalBody += `\n\n--- Data z KN ---\n${knData.trim()}`;
        }
      }

      await createActivity.mutateAsync({
        leadId: id,
        type: activityType,
        subject: activitySubject,
        body: finalBody || undefined,
      });
      
      toast.success('Aktivita přidána');
      setShowActivityForm(false);
      setActivitySubject('');
      setActivityBody('');
      setActivityType('note');
      setIsOwner(null);
      setKnData('');
    } catch (err) {
      toast.error('Nepodařilo se přidat aktivitu');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <p className="text-red-500">Lead nebyl nalezen</p>
          <Link to="/leads" className="mt-4 inline-block text-primary-600 hover:underline">
            Zpět na seznam
          </Link>
        </div>
      </Card>
    );
  }

  const contact = lead.contact;
  const fullName = `${contact.firstName} ${contact.lastName || ''}`.trim();

  const valuoPayload = lead.sourcePayload?.valuation;
  const valuoResponse: any = valuoPayload?.responsePayload;
  const valuoResult: any = valuoResponse?.result;
  const valuoStatus = valuoPayload?.status;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/leads"
          className="flex items-center justify-center rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
        </Link>
        <Avatar name={fullName} size="lg" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{fullName}</h1>
          <p className="text-sm text-gray-500">
            {lead.currentStage?.name || 'Bez fáze'} • {formatValue(Number(lead.expectedValue))}
          </p>
        </div>
        <div className="flex gap-2">
          {contact.email && (
            <a href={`mailto:${contact.email}`}>
              <Button variant="outline" size="sm">
                <EnvelopeIcon className="mr-2 h-4 w-4" />
                Email
              </Button>
            </a>
          )}
          {contact.phone && (
            sip.isConfigured ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => sip.makeCall(contact.phone!, contact.id, lead?.id)}
                disabled={sip.callState !== 'idle'}
              >
                <PhoneIcon className="mr-2 h-4 w-4" />
                {sip.callState !== 'idle' ? 'Hovor probíhá…' : 'Volat'}
              </Button>
            ) : (
              <a href={`tel:${contact.phone}`}>
                <Button variant="outline" size="sm">
                  <PhoneIcon className="mr-2 h-4 w-4" />
                  Volat
                </Button>
              </a>
            )
          )}
        </div>
      </div>

      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Kontakt</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <span className="text-sm text-gray-500">Jméno</span>
                  <p className="font-medium">{fullName}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Email</span>
                  <p className="font-medium">{contact.email || '-'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Telefon</span>
                  <p className="font-medium">{contact.phone || '-'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Vytvořeno</span>
                  <p className="font-medium">{formatDate(lead.createdAt)}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Formulářová data - custom pole z API submitu (sourcePayload.data) */}
          {(() => {
            const customData = (lead.sourcePayload as any)?.data;
            if (!customData || typeof customData !== 'object') return null;
            const entries = Object.entries(customData).filter(
              ([, v]) => v !== null && v !== undefined && v !== '',
            );
            if (entries.length === 0) return null;

            const prettyKey = (key: string): string => {
              return key
                .replace(/[-_]/g, ' ')
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/^./, c => c.toUpperCase());
            };

            const prettyValue = (v: unknown): string => {
              if (typeof v === 'boolean') return v ? 'Ano' : 'Ne';
              if (typeof v === 'number') return new Intl.NumberFormat('cs-CZ').format(v);
              return String(v);
            };

            return (
              <Card>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <DocumentTextIcon className="h-5 w-5 text-indigo-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Údaje z formuláře</h2>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {entries.map(([key, value]) => (
                      <div key={key} className={typeof value === 'string' && value.length > 60 ? 'sm:col-span-2' : ''}>
                        <span className="text-sm text-gray-500">{prettyKey(key)}</span>
                        <p className="font-medium">{prettyValue(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            );
          })()}

          {/* Nemovitost - z sourcePayload */}
          {(() => {
            if (!lead.sourcePayload) return null;
            
            const hasProperty = !!lead.sourcePayload?.property;
            const hasGPS = !!(((lead.sourcePayload as any)?.lat || lead.sourcePayload?.property?.lat));
            
            // Zobrazit kartu pokud existuje property NEBO GPS souřadnice
            if (!hasProperty && !hasGPS) return null;
            
            return (
              <Card>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <HomeIcon className="h-5 w-5 text-primary-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Nemovitost</h2>
                  </div>
                  
                  {/* Mapy.cz Panorama */}
                  {(() => {
                    const panoramaUrl = (lead.sourcePayload.property as any)?.panoramaUrl || (lead.sourcePayload as any).panoramaUrl;
                    const lat = lead.sourcePayload.property?.lat || (lead.sourcePayload as any).lat;
                    const lng = lead.sourcePayload.property?.lng || (lead.sourcePayload as any).lng;
                  
                  return (
                    <>
                      {/* Uložená panorama z Supabase Storage */}
                      {panoramaUrl && (
                        <div className="mb-4">
                          <div className="text-sm font-medium text-gray-700 mb-2">Uložená panorama</div>
                          <div className="rounded-lg overflow-hidden bg-gray-100">
                            <img 
                              src={panoramaUrl} 
                              alt="Uložená Street View panorama" 
                              className="w-full h-auto"
                              style={{ maxHeight: '400px', objectFit: 'cover' }}
                            />
                            <div className="text-xs text-gray-500 px-2 py-1">© Mapy.cz (uloženo)</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Živá panorama z API */}
                      {lat && lng && (
                        <div className="mb-4">
                          <div className="text-sm font-medium text-gray-700 mb-2">Interaktivní panorama - použijte myš pro rozhlížení</div>
                          <MapyCzPanoramaInteractive
                            lat={lat}
                            lng={lng}
                            height={500}
                            showNavigation={true}
                          />
                        </div>
                      )}
                    </>
                  );
                })()}
                
                <div className="grid gap-4 sm:grid-cols-2">
                  {lead.sourcePayload.property?.type && (
                    <div>
                      <span className="text-sm text-gray-500">Typ</span>
                      <p className="font-medium">
                        {PROPERTY_TYPE_LABELS[lead.sourcePayload.property.type] || lead.sourcePayload.property.type}
                      </p>
                    </div>
                  )}
                  {lead.sourcePayload.propertyDetails?.floorArea && (
                    <div>
                      <span className="text-sm text-gray-500">Plocha</span>
                      <p className="font-medium">{lead.sourcePayload.propertyDetails.floorArea} m²</p>
                    </div>
                  )}
                  {lead.sourcePayload.propertyDetails?.lotArea && (
                    <div>
                      <span className="text-sm text-gray-500">Plocha pozemku</span>
                      <p className="font-medium">{lead.sourcePayload.propertyDetails.lotArea} m²</p>
                    </div>
                  )}
                  {lead.sourcePayload.propertyDetails?.localType && (
                    <div>
                      <span className="text-sm text-gray-500">Dispozice</span>
                      <p className="font-medium">{lead.sourcePayload.propertyDetails.localType}</p>
                    </div>
                  )}
                  {lead.sourcePayload.propertyDetails?.rating && (
                    <div>
                      <span className="text-sm text-gray-500">Stav</span>
                      <p className="font-medium">
                        {RATING_LABELS[lead.sourcePayload.propertyDetails.rating] || lead.sourcePayload.propertyDetails.rating}
                      </p>
                    </div>
                  )}
                  {lead.sourcePayload.propertyDetails?.ownership && (
                    <div>
                      <span className="text-sm text-gray-500">Vlastnictví</span>
                      <p className="font-medium">
                        {OWNERSHIP_LABELS[lead.sourcePayload.propertyDetails.ownership] || lead.sourcePayload.propertyDetails.ownership}
                      </p>
                    </div>
                  )}
                  {lead.sourcePayload.propertyDetails?.houseType && (
                    <div>
                      <span className="text-sm text-gray-500">Typ domu</span>
                      <p className="font-medium">
                        {HOUSE_TYPE_LABELS[lead.sourcePayload.propertyDetails.houseType] || lead.sourcePayload.propertyDetails.houseType}
                      </p>
                    </div>
                  )}
                  {lead.sourcePayload.propertyDetails?.landType && (
                    <div>
                      <span className="text-sm text-gray-500">Typ pozemku</span>
                      <p className="font-medium">
                        {LAND_TYPE_LABELS[lead.sourcePayload.propertyDetails.landType] || lead.sourcePayload.propertyDetails.landType}
                      </p>
                    </div>
                  )}
                </div>

                {/* Property Images Upload */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Fotografie nemovitosti</h3>
                  <PropertyImageUpload
                    leadId={lead.id}
                    images={propertyImages}
                    onImagesChange={setPropertyImages}
                  />
                </div>
              </div>
            </Card>
            );
          })()}

          {/* Strukturovaná adresa - z sourcePayload */}
          {lead.sourcePayload?.address && (
            <Card>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPinIcon className="h-5 w-5 text-orange-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Adresa nemovitosti</h2>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  {(lead.sourcePayload.address.street || lead.sourcePayload.address.streetNumber) && (
                    <div className="sm:col-span-2">
                      <span className="text-sm text-gray-500">Ulice</span>
                      <p className="font-medium">
                        {[lead.sourcePayload.address.street, lead.sourcePayload.address.streetNumber].filter(Boolean).join(' ')}
                      </p>
                    </div>
                  )}
                  {lead.sourcePayload.address.city && (
                    <div>
                      <span className="text-sm text-gray-500">Město</span>
                      <p className="font-medium">{lead.sourcePayload.address.city}</p>
                    </div>
                  )}
                  {lead.sourcePayload.address.district && (
                    <div>
                      <span className="text-sm text-gray-500">Městská část</span>
                      <p className="font-medium">{lead.sourcePayload.address.district}</p>
                    </div>
                  )}
                  {lead.sourcePayload.address.postalCode && (
                    <div>
                      <span className="text-sm text-gray-500">PSČ</span>
                      <p className="font-medium">{lead.sourcePayload.address.postalCode}</p>
                    </div>
                  )}
                  {lead.sourcePayload.address.region && (
                    <div>
                      <span className="text-sm text-gray-500">Kraj</span>
                      <p className="font-medium">{lead.sourcePayload.address.region}</p>
                    </div>
                  )}
                  {(() => {
                    const lat = lead.sourcePayload.property?.lat || (lead.sourcePayload as any).lat;
                    const lng = lead.sourcePayload.property?.lng || (lead.sourcePayload as any).lng;
                    
                    if (lat && lng) {
                      return (
                        <div className="sm:col-span-2">
                          <span className="text-sm text-gray-500">GPS souřadnice</span>
                          <p className="font-medium font-mono text-sm">
                            {lat.toFixed(6)}, {lng.toFixed(6)}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </Card>
          )}

          {/* Ocenění - z sourcePayload */}
          {lead.sourcePayload?.valuation && (
            <Card>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Ocenění (Valuo)</h2>
                  {valuoStatus && (
                    <Badge
                      variant={
                        valuoStatus === 'completed'
                          ? 'success'
                          : valuoStatus === 'failed'
                            ? 'danger'
                            : 'default'
                      }
                    >
                      {valuoStatus}
                    </Badge>
                  )}
                </div>

                {valuoStatus === 'failed' && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-4">
                    <p className="text-sm font-medium text-red-700">
                      {String(valuoPayload?.errorMessage || '').startsWith('LIMIT_ZDARMA')
                        ? 'Odhad zdarma už návštěvník využil — Valuo se nevolalo, odhad připravte ručně'
                        : 'Ocenění selhalo'}
                    </p>
                    <p className="text-sm text-red-700 mt-1">{valuoPayload?.errorMessage || valuoPayload?.responsePayload?.errorMessage || valuoPayload?.responsePayload?.message || valuoPayload?.requestPayload?.error || lead.sourcePayload?.valuation?.responsePayload?.errorMessage || '-'}</p>
                    {lead.sourcePayload?.valuation?.requestPayload && (
                      <p className="text-xs text-red-700 mt-2 font-mono break-all">
                        request: {JSON.stringify(lead.sourcePayload.valuation.requestPayload)}
                      </p>
                    )}
                  </div>
                )}

                {valuoStatus === 'pending' && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
                    <p className="text-sm text-gray-700">
                      Ocenění je ve stavu <span className="font-medium">pending</span>. Pokud se nevyplní do 1 minuty, zkontroluj logy API.
                    </p>
                  </div>
                )}

                {valuoStatus === 'completed' && (valuoPayload?.avgPrice || valuoResult?.avg_price) && (
                  <div className="bg-green-50 rounded-lg p-4 mb-4">
                    <div className="text-center">
                      <span className="text-sm text-green-700">Odhadovaná cena</span>
                      <p className="text-2xl font-bold text-green-700">
                        {formatPrice(Number(valuoPayload?.avgPrice ?? valuoResult?.avg_price))}
                      </p>
                      <p className="text-sm text-green-600">
                        {formatPrice(Number(valuoPayload?.minPrice ?? valuoResult?.min_price ?? 0))} - {formatPrice(Number(valuoPayload?.maxPrice ?? valuoResult?.max_price ?? 0))}
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <span className="text-sm text-gray-500">Cena za m²</span>
                    <p className="font-medium">
                      {valuoPayload?.avgPriceM2 || valuoResult?.avg_price_m2
                        ? formatPrice(Number(valuoPayload?.avgPriceM2 ?? valuoResult?.avg_price_m2))
                        : '-'}
                    </p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Započítaná plocha</span>
                    <p className="font-medium">
                      {valuoPayload?.calcArea || valuoResult?.calc_area ? `${Number(valuoPayload?.calcArea ?? valuoResult?.calc_area)} m²` : '-'}
                    </p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Rozsah (range price)</span>
                    <p className="font-medium">
                      {Array.isArray(valuoResult?.range_price)
                        ? `${formatPrice(Number(valuoResult.range_price[0]))} - ${formatPrice(Number(valuoResult.range_price[1]))}`
                        : '-'}
                    </p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Rozsah za m² (range price m²)</span>
                    <p className="font-medium">
                      {Array.isArray(valuoResult?.range_price_m2)
                        ? `${formatPrice(Number(valuoResult.range_price_m2[0]))} - ${formatPrice(Number(valuoResult.range_price_m2[1]))}`
                        : '-'}
                    </p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Min/Max za m²</span>
                    <p className="font-medium">
                      {valuoResult?.min_price_m2 || valuoResult?.max_price_m2
                        ? `${formatPrice(Number(valuoResult.min_price_m2 ?? 0))} - ${formatPrice(Number(valuoResult.max_price_m2 ?? 0))}`
                        : '-'}
                    </p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Odchylka (std m²)</span>
                    <p className="font-medium">{formatNumber(valuoResult?.std_price_m2)}</p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Skóre podobnosti</span>
                    <p className="font-medium">{valuoResult?.avg_score !== undefined ? String(valuoResult.avg_score) : '-'}</p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Vzdálenost (radius)</span>
                    <p className="font-medium">{valuoResult?.distance ? `${formatNumber(valuoResult.distance)} m` : '-'}</p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Prům. vzdálenost</span>
                    <p className="font-medium">{valuoResult?.avg_distance ? `${formatNumber(valuoResult.avg_distance)} m` : '-'}</p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Prům. stáří dat</span>
                    <p className="font-medium">{valuoResult?.avg_age ? `${formatNumber(valuoResult.avg_age)} dní` : '-'}</p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Prům. doba inzerce</span>
                    <p className="font-medium">{valuoResult?.avg_duration ? `${formatNumber(valuoResult.avg_duration)} dní` : '-'}</p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Měna</span>
                    <p className="font-medium">{valuoResult?.currency || '-'}</p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Datum výpočtu (as_of)</span>
                    <p className="font-medium">{valuoResult?.as_of ? formatDateTime(valuoResult.as_of) : '-'}</p>
                  </div>

                  <div className="sm:col-span-2">
                    <span className="text-sm text-gray-500">Flags</span>
                    <p className="font-medium">
                      {Array.isArray(valuoResult?.flags) && valuoResult.flags.length > 0 ? valuoResult.flags.join(', ') : '-'}
                    </p>
                  </div>

                  <div className="sm:col-span-2">
                    <span className="text-sm text-gray-500">Použité inzeráty (keep_ids)</span>
                    <p className="font-medium">
                      {Array.isArray(valuoResponse?.keep_ids) ? `${valuoResponse.keep_ids.length} ks` : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* RUIAN/Katastr data - z sourcePayload */}
          {lead.sourcePayload?.cadastre && (lead.sourcePayload.cadastre.ruianCode || lead.sourcePayload.cadastre.ruianData) && (
            <Card>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">RUIAN data</h2>
                </div>
                
                {/* Adresní místo */}
                {lead.sourcePayload.cadastre.ruianData?.adresniMisto && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Adresní místo</h3>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {lead.sourcePayload.cadastre.ruianData.adresniMisto.adresa && (
                        <div className="col-span-full">
                          <span className="text-xs text-gray-500">Adresa</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.adresniMisto.adresa}</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.adresniMisto.cisloDomovni && (
                        <div>
                          <span className="text-xs text-gray-500">Číslo popisné</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.adresniMisto.cisloDomovni}</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.adresniMisto.cisloOrientacni && (
                        <div>
                          <span className="text-xs text-gray-500">Číslo orientační</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.adresniMisto.cisloOrientacni}</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.adresniMisto.psc && (
                        <div>
                          <span className="text-xs text-gray-500">PSČ</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.adresniMisto.psc}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Stavební objekt */}
                {lead.sourcePayload.cadastre.ruianData?.stavebniObjekt && (
                  <div className="mb-4 pt-4 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Stavební objekt</h3>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {lead.sourcePayload.cadastre.ruianData.stavebniObjekt.pocetPodlazi && (
                        <div>
                          <span className="text-xs text-gray-500">Počet podlaží</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.stavebniObjekt.pocetPodlazi}</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.stavebniObjekt.pocetBytu !== undefined && lead.sourcePayload.cadastre.ruianData.stavebniObjekt.pocetBytu > 0 && (
                        <div>
                          <span className="text-xs text-gray-500">Počet bytů</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.stavebniObjekt.pocetBytu}</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.stavebniObjekt.podlahovaPlocha && (
                        <div>
                          <span className="text-xs text-gray-500">Podlahová plocha</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.stavebniObjekt.podlahovaPlocha.toLocaleString('cs-CZ')} m²</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.stavebniObjekt.obestavenyprostor && (
                        <div>
                          <span className="text-xs text-gray-500">Obestavěný prostor</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.stavebniObjekt.obestavenyprostor.toLocaleString('cs-CZ')} m³</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.stavebniObjekt.druhKonstrukce && (
                        <div>
                          <span className="text-xs text-gray-500">Druh konstrukce</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.stavebniObjekt.druhKonstrukce}</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.stavebniObjekt.dokonceni && (
                        <div>
                          <span className="text-xs text-gray-500">Datum dokončení</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.stavebniObjekt.dokonceni}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Parcela */}
                {lead.sourcePayload.cadastre.ruianData?.parcela && (
                  <div className="mb-4 pt-4 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Parcela</h3>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {(lead.sourcePayload.cadastre.ruianData.parcela.kmenoveCislo || lead.sourcePayload.cadastre.parcelNumber) && (
                        <div>
                          <span className="text-xs text-gray-500">Číslo parcely</span>
                          <p className="font-medium text-sm">
                            {lead.sourcePayload.cadastre.parcelNumber || 
                              (lead.sourcePayload.cadastre.ruianData.parcela.poddeleniCisla 
                                ? `${lead.sourcePayload.cadastre.ruianData.parcela.kmenoveCislo}/${lead.sourcePayload.cadastre.ruianData.parcela.poddeleniCisla}`
                                : lead.sourcePayload.cadastre.ruianData.parcela.kmenoveCislo)}
                          </p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.parcela.vymera > 0 && (
                        <div>
                          <span className="text-xs text-gray-500">Výměra</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.parcela.vymera.toLocaleString('cs-CZ')} m²</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.parcela.druhPozemku && (
                        <div>
                          <span className="text-xs text-gray-500">Druh pozemku</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.parcela.druhPozemku}</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.parcela.zpusobVyuziti && (
                        <div>
                          <span className="text-xs text-gray-500">Způsob využití</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.parcela.zpusobVyuziti}</p>
                        </div>
                      )}
                      {(lead.sourcePayload.cadastre.cadastralArea || lead.sourcePayload.cadastre.ruianData.parcela.nazevKatastralnihoUzemi) && (
                        <div>
                          <span className="text-xs text-gray-500">Katastrální území</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.cadastralArea || lead.sourcePayload.cadastre.ruianData.parcela.nazevKatastralnihoUzemi}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Obec */}
                {lead.sourcePayload.cadastre.ruianData?.obec && (
                  <div className="pt-4 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Obec</h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {lead.sourcePayload.cadastre.ruianData.obec.nazev && (
                        <div>
                          <span className="text-xs text-gray-500">Název obce</span>
                          <p className="font-medium text-sm">{lead.sourcePayload.cadastre.ruianData.obec.nazev}</p>
                        </div>
                      )}
                      {lead.sourcePayload.cadastre.ruianData.obec.kod && (
                        <div>
                          <span className="text-xs text-gray-500">Kód obce</span>
                          <p className="font-medium text-sm font-mono">{lead.sourcePayload.cadastre.ruianData.obec.kod}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* RUIAN kód */}
                {lead.sourcePayload.cadastre.ruianCode && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-400">RUIAN kód: </span>
                    <span className="text-xs font-mono text-gray-500">{lead.sourcePayload.cadastre.ruianCode}</span>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Chytrá analýza dat */}
          {lead.sourcePayload && (() => {
            const propertyData = {
              propertyType: lead.sourcePayload.property?.type,
              houseType: lead.sourcePayload.propertyDetails?.houseType,
              area: lead.sourcePayload.propertyDetails?.floorArea,
              landArea: lead.sourcePayload.propertyDetails?.lotArea,
              condition: lead.sourcePayload.propertyDetails?.rating,
              localType: lead.sourcePayload.propertyDetails?.localType,
            };
            const ruianData = lead.sourcePayload.cadastre?.ruianData;
            const valuationData = valuoResult ? {
              avgScore: valuoResult.avg_score,
              keepIdsCount: Array.isArray(valuoResult.keep_ids) ? valuoResult.keep_ids.length : undefined,
              distance: valuoResult.distance,
              avgDistance: valuoResult.avg_distance,
              avgAge: valuoResult.avg_age,
              calcArea: valuoResult.calc_area,
            } : undefined;
            
            const insights = analyzeProperty(propertyData, ruianData, valuationData);
            
            if (insights.length === 0) return null;
            
            const getInsightIcon = (severity: InsightSeverity) => {
              switch (severity) {
                case 'error': return <ExclamationTriangleIcon className={`h-5 w-5 ${getSeverityIconClass(severity)}`} />;
                case 'warning': return <ExclamationTriangleIcon className={`h-5 w-5 ${getSeverityIconClass(severity)}`} />;
                case 'success': return <CheckBadgeIcon className={`h-5 w-5 ${getSeverityIconClass(severity)}`} />;
                default: return <InformationCircleIcon className={`h-5 w-5 ${getSeverityIconClass(severity)}`} />;
              }
            };
            
            return (
              <Card>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <LightBulbIcon className="h-5 w-5 text-amber-500" />
                    <h2 className="text-lg font-semibold text-gray-900">Analýza dat</h2>
                    <Badge variant={insights.some(i => i.severity === 'error') ? 'danger' : insights.some(i => i.severity === 'warning') ? 'warning' : 'success'}>
                      {insights.filter(i => i.severity === 'error').length + insights.filter(i => i.severity === 'warning').length} upozornění
                    </Badge>
                  </div>
                  
                  <div className="space-y-3">
                    {insights.map((insight) => (
                      <div
                        key={insight.id}
                        className={`rounded-lg border p-4 ${getSeverityBgClass(insight.severity)}`}
                      >
                        <div className="flex items-start gap-3">
                          {getInsightIcon(insight.severity)}
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-medium ${getSeverityTextClass(insight.severity)}`}>
                              {insight.title}
                            </h4>
                            <p className={`text-sm mt-1 ${getSeverityTextClass(insight.severity)} opacity-90`}>
                              {insight.description}
                            </p>
                            {insight.suggestion && (
                              <p className={`text-sm mt-2 font-medium ${getSeverityTextClass(insight.severity)}`}>
                                Doporuceni: {insight.suggestion}
                              </p>
                            )}
                            {insight.data && Object.keys(insight.data).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                                {Object.entries(insight.data).map(([key, value]) => (
                                  <span key={key} className={`text-xs ${getSeverityTextClass(insight.severity)} opacity-75`}>
                                    <span className="font-medium">{key}:</span> {value}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            );
          })()}

          {/* Hypotéka - z sourcePayload */}
          {lead.sourcePayload?.mortgage && (
            <Card>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CalculatorIcon className="h-5 w-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Hypotéka</h2>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="text-center">
                    <span className="text-sm text-blue-700">Měsíční splátka</span>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatPrice(lead.sourcePayload.mortgage.monthlyPayment)}
                    </p>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <span className="text-sm text-gray-500">Cena nemovitosti</span>
                    <p className="font-medium">{formatPrice(lead.sourcePayload.mortgage.propertyPrice)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Vlastní prostředky</span>
                    <p className="font-medium">{formatPrice(lead.sourcePayload.mortgage.ownFunds)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Výše úvěru</span>
                    <p className="font-medium">{formatPrice(lead.sourcePayload.mortgage.loanAmount)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Úroková sazba</span>
                    <p className="font-medium">{lead.sourcePayload.mortgage.interestRate.toFixed(2)} %</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Doba splácení</span>
                    <p className="font-medium">{lead.sourcePayload.mortgage.loanTerm} let</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Fixace</span>
                    <p className="font-medium">{lead.sourcePayload.mortgage.fixation} let</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Celkem zaplatí</span>
                    <p className="font-medium">{formatPrice(lead.sourcePayload.mortgage.totalPayment)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Celkový úrok</span>
                    <p className="font-medium">{formatPrice(lead.sourcePayload.mortgage.totalInterest)}</p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Hypokalkulačka - z sourcePayload (formType: hypokalkulacka) */}
          {(lead.sourcePayload as any)?.formType === 'hypokalkulacka' && (
            <Card>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CalculatorIcon className="h-5 w-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Hypokalkulačka</h2>
                  {(lead.sourcePayload as any)?.approvalStatus && (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      (lead.sourcePayload as any).approvalStatus === 'green' ? 'bg-green-100 text-green-800' :
                      (lead.sourcePayload as any).approvalStatus === 'orange' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {(lead.sourcePayload as any).approvalStatus === 'green' ? 'Vypadá dobře' :
                       (lead.sourcePayload as any).approvalStatus === 'orange' ? 'Hraniční' : 'Problematické'}
                    </span>
                  )}
                </div>
                
                {(lead.sourcePayload as any)?.monthlyPayment && (
                  <div className="bg-blue-50 rounded-lg p-4 mb-4">
                    <div className="text-center">
                      <span className="text-sm text-blue-700">Odhadovaná měsíční splátka</span>
                      <p className="text-2xl font-bold text-blue-700">
                        {formatPrice((lead.sourcePayload as any).monthlyPayment)}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="grid gap-4 sm:grid-cols-2">
                  {(lead.sourcePayload as any)?.purpose && (
                    <div>
                      <span className="text-sm text-gray-500">Účel</span>
                      <p className="font-medium">
                        {{
                          purchase: 'Koupě nemovitosti',
                          refinance: 'Refinancování',
                          construction: 'Stavba',
                          investment: 'Investice',
                        }[(lead.sourcePayload as any).purpose as string] || (lead.sourcePayload as any).purpose}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.propertyPrice && (
                    <div>
                      <span className="text-sm text-gray-500">Cena nemovitosti</span>
                      <p className="font-medium">{formatPrice((lead.sourcePayload as any).propertyPrice)}</p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.ownFunds && (
                    <div>
                      <span className="text-sm text-gray-500">Vlastní prostředky</span>
                      <p className="font-medium">{formatPrice((lead.sourcePayload as any).ownFunds)}</p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.loanAmount && (
                    <div>
                      <span className="text-sm text-gray-500">Požadovaný úvěr</span>
                      <p className="font-medium">{formatPrice((lead.sourcePayload as any).loanAmount)}</p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.maxLoanAmount && (
                    <div>
                      <span className="text-sm text-gray-500">Max. možný úvěr</span>
                      <p className="font-medium">{formatPrice((lead.sourcePayload as any).maxLoanAmount)}</p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.householdIncome && (
                    <div>
                      <span className="text-sm text-gray-500">Příjem domácnosti</span>
                      <p className="font-medium">
                        {{
                          'under50': 'do 50 000 Kč',
                          '50-70': '50 000 – 70 000 Kč',
                          '70-90': '70 000 – 90 000 Kč',
                          '90-120': '90 000 – 120 000 Kč',
                          '120plus': '120 000+ Kč',
                        }[(lead.sourcePayload as any).householdIncome as string] || (lead.sourcePayload as any).householdIncome}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.ageGroup && (
                    <div>
                      <span className="text-sm text-gray-500">Věková skupina</span>
                      <p className="font-medium">
                        {(lead.sourcePayload as any).ageGroup === 'under36' ? 'Do 36 let' : '36+ let'}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.hasLiabilities !== undefined && (
                    <div>
                      <span className="text-sm text-gray-500">Má závazky</span>
                      <p className="font-medium">
                        {(lead.sourcePayload as any).hasLiabilities ? 
                          ((lead.sourcePayload as any).liabilitiesLevel === 'small' ? 'Ano - malé' :
                           (lead.sourcePayload as any).liabilitiesLevel === 'medium' ? 'Ano - střední' :
                           (lead.sourcePayload as any).liabilitiesLevel === 'high' ? 'Ano - vysoké' : 'Ano') 
                          : 'Ne'}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.incomeType && (
                    <div>
                      <span className="text-sm text-gray-500">Typ příjmu</span>
                      <p className="font-medium">
                        {{
                          employee: 'Zaměstnanec',
                          'self-employed': 'OSVČ',
                          combined: 'Kombinace',
                          maternity: 'Mateřská',
                          other: 'Jiné',
                        }[(lead.sourcePayload as any).incomeType as string] || (lead.sourcePayload as any).incomeType}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.timeline && (
                    <div>
                      <span className="text-sm text-gray-500">Časový horizont</span>
                      <p className="font-medium">
                        {{
                          now: 'Hned',
                          '1month': 'Do měsíce',
                          '1-3months': '1-3 měsíce',
                          later: 'Později',
                        }[(lead.sourcePayload as any).timeline as string] || (lead.sourcePayload as any).timeline}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.preferredContact && (
                    <div>
                      <span className="text-sm text-gray-500">Preferovaný kontakt</span>
                      <p className="font-medium">
                        {{
                          email: 'Email',
                          phone: 'Telefon',
                          whatsapp: 'WhatsApp',
                        }[(lead.sourcePayload as any).preferredContact as string] || (lead.sourcePayload as any).preferredContact}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Prescoring - z sourcePayload (formType: prescoring) */}
          {(lead.sourcePayload as any)?.formType === 'prescoring' && (
            <Card>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CalculatorIcon className="h-5 w-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Prescoring</h2>
                  {(lead.sourcePayload as any)?.status && (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      (lead.sourcePayload as any).status === 'green' ? 'bg-green-100 text-green-800' :
                      (lead.sourcePayload as any).status === 'orange' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {(lead.sourcePayload as any).status === 'green' ? 'Vypadá dobře' :
                       (lead.sourcePayload as any).status === 'orange' ? 'Hraniční' : 'Problematické'}
                    </span>
                  )}
                </div>
                
                {(lead.sourcePayload as any)?.score !== undefined && (
                  <div className={`rounded-lg p-4 mb-4 ${
                    (lead.sourcePayload as any).status === 'green' ? 'bg-green-50' :
                    (lead.sourcePayload as any).status === 'orange' ? 'bg-yellow-50' : 'bg-red-50'
                  }`}>
                    <div className="text-center">
                      <span className={`text-sm ${
                        (lead.sourcePayload as any).status === 'green' ? 'text-green-700' :
                        (lead.sourcePayload as any).status === 'orange' ? 'text-yellow-700' : 'text-red-700'
                      }`}>Skóre</span>
                      <p className={`text-2xl font-bold ${
                        (lead.sourcePayload as any).status === 'green' ? 'text-green-700' :
                        (lead.sourcePayload as any).status === 'orange' ? 'text-yellow-700' : 'text-red-700'
                      }`}>
                        {(lead.sourcePayload as any).score}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="grid gap-4 sm:grid-cols-2">
                  {(lead.sourcePayload as any)?.purpose && (
                    <div>
                      <span className="text-sm text-gray-500">Účel</span>
                      <p className="font-medium">
                        {{
                          purchase: 'Koupě nemovitosti',
                          refinance: 'Refinancování',
                          construction: 'Stavba',
                        }[(lead.sourcePayload as any).purpose as string] || (lead.sourcePayload as any).purpose}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.householdIncome && (
                    <div>
                      <span className="text-sm text-gray-500">Příjem domácnosti</span>
                      <p className="font-medium">
                        {{
                          under50: 'do 50 000 Kč',
                          '50-80': '50 000 – 80 000 Kč',
                          '80-120': '80 000 – 120 000 Kč',
                          '120plus': '120 000+ Kč',
                        }[(lead.sourcePayload as any).householdIncome as string] || (lead.sourcePayload as any).householdIncome}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.monthlyLiabilities && (
                    <div>
                      <span className="text-sm text-gray-500">Měsíční závazky</span>
                      <p className="font-medium">
                        {{
                          none: 'Žádné',
                          under5: 'do 5 000 Kč',
                          '5-15': '5 000 – 15 000 Kč',
                          '15plus': '15 000+ Kč',
                        }[(lead.sourcePayload as any).monthlyLiabilities as string] || (lead.sourcePayload as any).monthlyLiabilities}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.ownFundsPercent && (
                    <div>
                      <span className="text-sm text-gray-500">Vlastní zdroje</span>
                      <p className="font-medium">
                        {{
                          under10: 'do 10 %',
                          '10-20': '10 – 20 %',
                          '20plus': '20 %+',
                          unknown: 'Nevím',
                        }[(lead.sourcePayload as any).ownFundsPercent as string] || (lead.sourcePayload as any).ownFundsPercent}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.incomeType && (
                    <div>
                      <span className="text-sm text-gray-500">Typ příjmu</span>
                      <p className="font-medium">
                        {{
                          employee: 'Zaměstnanec',
                          'self-employed': 'OSVČ',
                          combined: 'Kombinace',
                          unknown: 'Nevím',
                        }[(lead.sourcePayload as any).incomeType as string] || (lead.sourcePayload as any).incomeType}
                      </p>
                    </div>
                  )}
                  {(lead.sourcePayload as any)?.contactPreference && (
                    <div>
                      <span className="text-sm text-gray-500">Preference kontaktu</span>
                      <p className="font-medium">
                        {(lead.sourcePayload as any).contactPreference === 'pdf-only' ? 'Pouze PDF' : 'Chci i zavolat'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Visitor Metadata - z sourcePayload._visitorMetadata nebo formSubmissions */}
          {(lead.sourcePayload as any)?._visitorMetadata && (
            <VisitorMetadata metadata={(lead.sourcePayload as any)._visitorMetadata} />
          )}

          {lead.notes && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Poznámky</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{lead.notes}</p>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Stav</h2>
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-gray-500">Fáze</span>
                  <div className="mt-1">
                    <select
                      value={lead.currentStageId || ''}
                      onChange={(e) => handleStageChange(e.target.value)}
                      disabled={updateLead.isPending}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                      style={{ 
                        borderColor: lead.currentStage?.color || '#d1d5db',
                      }}
                    >
                      {pipeline?.stages?.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                          {stage.isCloseWon ? ' ✓' : stage.isCloseLost ? ' ✗' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Status</span>
                  <div className="mt-1">
                    <span 
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        lead.status === 'won' ? 'bg-green-100 text-green-800' :
                        lead.status === 'lost' ? 'bg-red-100 text-red-800' :
                        lead.status === 'spam' ? 'bg-yellow-100 text-yellow-800' :
                        lead.status === 'archived' ? 'bg-gray-100 text-gray-800' :
                        'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {LEAD_STATUS_LABELS[lead.status] || lead.status}
                    </span>
                    {/*
                      Proč byl lead označen automaticky. Bez toho nejde
                      poznat falešně pozitivní klasifikaci ani doladit práh.
                    */}
                    {lead.sourcePayload?._spamCheck && (
                      <div className="mt-2 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2">
                        <p className="text-xs font-medium text-yellow-800">
                          Spam skóre: {lead.sourcePayload._spamCheck.score}/100
                          {lead.sourcePayload._spamCheck.score >= 60 && ' — označeno automaticky'}
                        </p>
                        {lead.sourcePayload._spamCheck.reasons?.length > 0 && (
                          <p className="mt-1 text-xs text-yellow-700">
                            {lead.sourcePayload._spamCheck.reasons.join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                    {lead.status === 'open' && (
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={async () => {
                            if (!id) return;
                            try {
                              await updateLead.mutateAsync({ id, data: { status: 'spam' } });
                              toast.success('Lead označen jako spam');
                            } catch { toast.error('Nepodařilo se změnit status'); }
                          }}
                          disabled={updateLead.isPending}
                          className="flex-1 px-2 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors disabled:opacity-50"
                        >
                          Spam
                        </button>
                        <button
                          onClick={async () => {
                            if (!id) return;
                            try {
                              await updateLead.mutateAsync({ id, data: { status: 'archived' } });
                              toast.success('Lead archivován');
                            } catch { toast.error('Nepodařilo se archivovat'); }
                          }}
                          disabled={updateLead.isPending}
                          className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                          Archivovat
                        </button>
                      </div>
                    )}
                    {(lead.status === 'spam' || lead.status === 'archived') && (
                      <button
                        onClick={async () => {
                          if (!id) return;
                          try {
                            await updateLead.mutateAsync({ id, data: { status: 'open' } });
                            toast.success('Lead obnoven');
                          } catch { toast.error('Nepodařilo se obnovit lead'); }
                        }}
                        disabled={updateLead.isPending}
                        className="mt-2 w-full px-2 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                      >
                        Obnovit lead
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Hodnota</span>
                  <p className="font-medium text-lg">{formatValue(Number(lead.expectedValue))}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Typ</span>
                  <p className="font-medium">{LEAD_TYPE_LABELS[lead.leadType] || lead.leadType}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Historie změn fází */}
          {stageHistory && stageHistory.length > 0 && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Historie fází</h2>
                <div className="space-y-3">
                  {stageHistory.slice(0, 5).map((item) => (
                    <div key={item.id} className="flex items-start gap-3 text-sm">
                      <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full" style={{ backgroundColor: item.toStage.color }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900">
                          {item.fromStage ? (
                            <>
                              <span style={{ color: item.fromStage.color }}>{item.fromStage.name}</span>
                              {' → '}
                            </>
                          ) : 'Vytvořeno → '}
                          <span style={{ color: item.toStage.color }}>{item.toStage.name}</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDateTime(item.changedAt)}
                          {item.user && ` • ${item.user.fullName}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Projekt</h2>
              {lead.project ? (
                <Link to={`/projects/${lead.project.id}`} className="text-primary-600 hover:underline">
                  {lead.project.name}
                </Link>
              ) : (
                <p className="text-gray-500">-</p>
              )}
            </div>
          </Card>

          {lead.source && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Zdroj</h2>
                <p className="font-medium">{lead.source.name}</p>
                <p className="text-sm text-gray-500">{lead.source.code}</p>
              </div>
            </Card>
          )}

          {/* Typ formuláře a metadata */}
          {lead.sourcePayload?.source && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Původ dat</h2>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-500">Typ formuláře</span>
                    <p className="font-medium">
                      {lead.sourcePayload.source === 'valuo_form' && 'Ocenění nemovitosti (Valuo)'}
                      {lead.sourcePayload.source === 'mortgage_form' && 'Hypoteční kalkulačka'}
                      {lead.sourcePayload.source === 'contact_form' && 'Kontaktní formulář'}
                      {!['valuo_form', 'mortgage_form', 'contact_form'].includes(lead.sourcePayload.source) && lead.sourcePayload.source}
                    </p>
                  </div>
                  {lead.sourcePayload.formCode && (
                    <div>
                      <span className="text-sm text-gray-500">Kód formuláře</span>
                      <p className="font-medium font-mono text-sm">{lead.sourcePayload.formCode}</p>
                    </div>
                  )}
                  {lead.sourcePayload.createdAt && (
                    <div>
                      <span className="text-sm text-gray-500">Odesláno</span>
                      <p className="font-medium">{formatDate(lead.sourcePayload.createdAt)}</p>
                    </div>
                  )}
                  {lead.sourcePayload.valuationId && (
                    <div>
                      <span className="text-sm text-gray-500">ID ocenění</span>
                      <p className="font-medium font-mono text-xs text-gray-600 break-all">{lead.sourcePayload.valuationId}</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Aktivity - přesunuté pod Stav s modrým borderem */}
          <Card className="border-2 border-blue-500">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Aktivity</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowActivityForm(!showActivityForm)}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Přidat
                </Button>
              </div>

              {showActivityForm && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    {ACTIVITY_TYPES.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => setActivityType(type.value)}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          activityType === type.value
                            ? 'bg-primary-100 border-primary-500 text-primary-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                  <Input
                    placeholder="Předmět aktivity..."
                    value={activitySubject}
                    onChange={(e) => setActivitySubject(e.target.value)}
                  />

                  {/* Speciální pole pro Ověření vlastníka */}
                  {activityType === 'owner_verification' && (
                    <div className="space-y-3 p-3 bg-white rounded-lg border border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Výsledek ověření
                        </label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="isOwner"
                              checked={isOwner === true}
                              onChange={() => setIsOwner(true)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">Je vlastník</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="isOwner"
                              checked={isOwner === false}
                              onChange={() => setIsOwner(false)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">Není vlastník</span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data z katastru nemovitostí (volitelné)
                        </label>
                        <textarea
                          placeholder="Vložte data z KN (např. LV, parcela, vlastník...)&#10;&#10;Příklad:&#10;LV: 1234&#10;Parcela: 123/4&#10;Vlastník: Jan Novák"
                          value={knData}
                          onChange={(e) => setKnData(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
                          rows={4}
                        />
                      </div>
                    </div>
                  )}

                  <textarea
                    placeholder="Popis (volitelné)..."
                    value={activityBody}
                    onChange={(e) => setActivityBody(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows={3}
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowActivityForm(false)}
                    >
                      Zrušit
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleCreateActivity}
                      disabled={!activitySubject.trim() || createActivity.isPending}
                    >
                      {createActivity.isPending ? 'Ukládám...' : 'Uložit'}
                    </Button>
                  </div>
                </div>
              )}

              {activitiesLoading ? (
                <div className="flex justify-center py-4">
                  <Spinner size="sm" />
                </div>
              ) : activities && activities.length > 0 ? (
                <div className="space-y-3">
                  {activities.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          {activity.type === 'call' && <PhoneIcon className="h-4 w-4 text-primary-600" />}
                          {activity.type === 'email' && <EnvelopeIcon className="h-4 w-4 text-primary-600" />}
                          {activity.type === 'meeting' && <CalendarIcon className="h-4 w-4 text-primary-600" />}
                          {activity.type === 'note' && <DocumentTextIcon className="h-4 w-4 text-primary-600" />}
                          {activity.type === 'task' && <CheckCircleIcon className="h-4 w-4 text-primary-600" />}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-primary-600">
                            {ACTIVITY_TYPE_LABELS[activity.type as ActivityType] || activity.type}
                          </span>
                          {activity.user?.fullName && (
                            <span className="text-xs text-gray-500">
                              • {activity.user.fullName}
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            • {formatDateTime(activity.createdAt)}
                          </span>
                        </div>
                        <p className="font-medium text-gray-900 text-sm">{activity.subject}</p>
                        {activity.body && (
                          <p className="text-sm text-gray-600 mt-1">{activity.body}</p>
                        )}
                        {/* Call metadata & recording */}
                        {activity.type === 'call' && activity.meta && (
                          <div className="mt-2 flex flex-wrap items-center gap-3">
                            {(activity.meta as any).duration > 0 && (
                              <span className="text-xs text-gray-500">
                                {Math.floor((activity.meta as any).duration / 60)}:{String((activity.meta as any).duration % 60).padStart(2, '0')} min
                              </span>
                            )}
                            {(activity.meta as any).callDirection && (
                              <span className="text-xs text-gray-400">
                                {(activity.meta as any).callDirection === 'outbound' ? '→ Odchozí' : '← Příchozí'}
                              </span>
                            )}
                            {(activity.meta as any).callStatus && (
                              <Badge variant={(activity.meta as any).callStatus === 'completed' ? 'success' : 'warning'} size="sm">
                                {(activity.meta as any).callStatus === 'completed' ? 'Dokončeno' : (activity.meta as any).callStatus === 'missed' ? 'Zmeškaný' : (activity.meta as any).callStatus}
                              </Badge>
                            )}
                            {(activity.meta as any).recordingUrl && (
                              <audio
                                controls
                                preload="none"
                                className="h-8 max-w-[260px]"
                                src={(activity.meta as any).recordingUrl}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Zatím žádné aktivity
                </div>
              )}
            </div>
          </Card>

          {/* Návody a dokumentace */}
          {documents && documents.length > 0 && (
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <BookOpenIcon className="h-5 w-5 text-primary-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Návody</h2>
                  </div>
                  <Link
                    to="/guides"
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    Zobrazit všechny
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </Link>
                </div>

                <div className="space-y-2">
                  {documents.slice(0, 5).map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => setViewingDocument(doc)}
                      className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-left transition-colors"
                    >
                      <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{doc.title}</p>
                        {doc.description && (
                          <p className="text-xs text-gray-500 truncate">{doc.description}</p>
                        )}
                      </div>
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        document={viewingDocument}
        isOpen={!!viewingDocument}
        onClose={() => setViewingDocument(null)}
      />
    </div>
  );
}
